import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  AttachPartnerParams,
  ControlCode,
  getControlName,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Form,
  InputFormField,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import mapValues from "lodash/mapValues";
import { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import { InlineFileFormField } from "./inline-file-form-field";

type FieldValues<K extends string> = Record<K | "instanceName", string>;

type FormConfig<K extends string> = Record<K, FieldConfig>;

type TransformParams<K extends string> = (
  data: FieldValues<K>,
) => Omit<AttachPartnerParams, "partnerCode">;

type FormProps<K extends string> = {
  control: ControlCode;
  instanceId?: string;
  instanceName?: string;
  description: ReactNode;
  help?: ReactNode;
  fields: FormConfig<K>;
  transformParams: TransformParams<K>;
  children?: ReactNode;
  extraOptions?: ReactNode;
};

interface FieldConfig {
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  validator: z.ZodSchema<string>;
}

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const entries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

export function IntegrationForm<K extends string>({
  control,
  instanceId,
  instanceName,
  description,
  help,
  fields,
  transformParams,
  children,
  extraOptions,
}: FormProps<K>) {
  const authConfig = useAuthConfig();
  const overlay = useOverlay();

  const controlName = getControlName(control);

  const { mutate: connect, isPending: isConnecting } = useMutation({
    mutationFn: async (data: FieldValues<K>) => {
      const params = transformParams(data);

      await service(authConfig).partner.attachPartner({
        partnerCode: control,
        instanceId,
        name: data.instanceName,
        ...params,
      });
    },
    onSuccess: async () => {
      notifySuccess({ message: `${controlName} successfully added.` });
      void overlay.close();
    },
    onError: (error) => {
      notifyError({ message: `Failed to connect to ${controlName}.`, error });
    },
  });

  const form = useForm<Record<string, string>>({
    resolver: zodResolver(
      z.object({
        instanceName: z.string(),
        ...mapValues(fields, (field) => field.validator),
      }),
    ),
    defaultValues: {
      instanceName: instanceName ?? "",
      ...mapValues(fields, (field) => field.defaultValue ?? ""),
    },
  });

  const { register } = form;

  const onSubmit = (data: Record<string, string>) =>
    connect(data as FieldValues<K>);

  return (
    <div className="flex h-full w-full flex-col gap-4 p-4">
      <div className="flex flex-col gap-4">
        <p className="m-0 leading-6 text-primary-foreground">{description}</p>

        {extraOptions}

        <Form
          {...form}
          onSubmitValid={onSubmit}
          className="flex max-w-md flex-col gap-2"
        >
          <InputFormField
            {...register("instanceName")}
            label="Integration Name"
            placeholder={controlName}
          />

          {entries(fields).map(([key, field]) =>
            field.type === "file" ? (
              <InlineFileFormField
                {...register(key, { required: field.required })}
                {...field}
                key={key}
                name={key}
                required={field.required}
              />
            ) : (
              <InputFormField
                {...register(key, { required: field.required })}
                key={key}
                type={field.type ?? "text"}
                label={field.label}
                required={field.required}
                placeholder={field.placeholder}
              />
            ),
          )}

          <div>{help}</div>

          <div className="flex gap-4">
            <Button
              type="submit"
              loading={isConnecting}
              disabled={isConnecting}
              intent="primary"
            >
              {instanceId ? "Save Changes" : "Connect"}
            </Button>
            <Button
              disabled={isConnecting}
              intent="secondary"
              onClick={overlay.close}
              type="button"
            >
              Go back
            </Button>
          </div>
        </Form>
      </div>

      {children}
    </div>
  );
}
